import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  ChangeEvent,
} from "react";
import { useDebounce } from "use-debounce";

import { Input, InputProps } from "../input";

export interface SearchInputProps extends InputProps {
  debounceMs: number;
}

export function SearchInput({
  value,
  onChange,
  debounceMs,
  ...props
}: SearchInputProps) {
  const [inputValue, setInputValue] = useState(value);
  const [debouncedValue] = useDebounce(inputValue, debounceMs);
  /**
   * Set only by the field's own onChange, cleared once that edit has been
   * reported. It is what separates "the user typed this" from "the owner handed
   * us a new value", and comparing values alone cannot make that distinction:
   * on the render that adopts an external value the effects still close over the
   * OLD `inputValue`, so a value-only guard emits the stale term before the state
   * update lands — which is precisely the race this component kept losing.
   */
  const hasUserEdit = useRef(false);

  const handleSetInputValue = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      hasUserEdit.current = true;
      setInputValue(e.target.value);
    },
    []
  );

  // Adopt an external change — a restored bare visit, browser back/forward, a
  // clear-all elsewhere on the page. Skipped while the user has an unreported
  // edit in flight, so their typing is never yanked out from under them.
  useEffect(() => {
    if (hasUserEdit.current) return;
    setInputValue((current) => (current === value ? current : value));
  }, [value]);

  // Report the user's own typing, once it settles. An adoption can never reach
  // here, because only the field's onChange arms it.
  useEffect(() => {
    if (!hasUserEdit.current) return;
    // Wait for the debounce to catch up with what the field actually shows.
    if (debouncedValue !== inputValue) return;

    hasUserEdit.current = false;
    if (debouncedValue === value) return;

    const syntheticEvent = {
      target: { value: debouncedValue },
      currentTarget: { value: debouncedValue },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(syntheticEvent);
  }, [debouncedValue, inputValue, value, onChange]);

  return <Input {...props} value={inputValue} onChange={handleSetInputValue} />;
}
